import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import type { Session } from '@supabase/supabase-js';

interface AppUser {
  id: string;
  name: string | null;
  email: string | null;
  isAdmin: boolean;
  accessLevel?: number;
  contractedServices?: string[];
}

interface Profile {
  id: string;
  email: string | null;
  first_name: string | null;
  last_name: string | null;
  full_name: string | null;
  access_level: number;
  contracted_services: string[] | null;
  profile_image_url: string | null;
  accepted_privacy_policy: boolean;
  accepted_terms_of_service: boolean;
}

export function useAuth() {
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isAdminRole, setIsAdminRole] = useState(false);
  const [mfaLevel, setMfaLevel] = useState<string>('aal1');

  const loadUserState = async (activeSession: Session | null) => {
    if (!activeSession?.user) {
      setSession(null);
      setProfile(null);
      setIsAdminRole(false);
      setMfaLevel('aal1');
      setIsLoading(false);
      return;
    }

    setSession(activeSession);

    try {
      const [{ data: profileData, error: profileError }, { data: roleData, error: roleError }] = await Promise.all([
        supabase
          .from('profiles')
          .select('*')
          .eq('id', activeSession.user.id)
          .single(),
        supabase
          .from('user_roles')
          .select('role')
          .eq('user_id', activeSession.user.id),
      ]);

      if (profileError) console.warn('useAuth: profile fetch failed', profileError.message);
      if (roleError) console.warn('useAuth: roles fetch failed', roleError.message);

      let finalProfile = profileData as Profile | null;
      // Social sign-up: persist the terms acceptance given on the registration screen
      let pendingAt: string | null = null;
      try { pendingAt = localStorage.getItem('cf_pending_terms_acceptance'); } catch { /* ignore */ }
      if (pendingAt && finalProfile) {
        if (!finalProfile.accepted_terms_of_service) {
          const { error: accErr } = await supabase.from('profiles').update({
            accepted_terms_of_service: true,
            accepted_privacy_policy: true,
            terms_accepted_at: pendingAt,
          }).eq('id', activeSession.user.id);
          if (!accErr) finalProfile = { ...finalProfile, accepted_terms_of_service: true, accepted_privacy_policy: true };
        }
        try { localStorage.removeItem('cf_pending_terms_acceptance'); } catch { /* ignore */ }
      }
      setProfile(finalProfile);
      setIsAdminRole(roleData?.some((r: any) => r.role === 'admin') || false);
      await refreshMfaLevel();
    } catch (err) {
      console.error('useAuth: loadUserState error', err);
    } finally {
      setIsLoading(false);
    }
  };

  const refreshMfaLevel = async () => {
    try {
      const { data } = await supabase.auth.mfa.getAuthenticatorAssuranceLevel();
      setMfaLevel(data?.currentLevel || 'aal1');
    } catch {
      setMfaLevel('aal1');
    }
  };

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (_event: string, newSession: Session | null) => {
        setTimeout(() => {
          loadUserState(newSession);
        }, 0);
      }
    );

    supabase.auth.getSession().then(({ data: { session: existingSession } }: { data: { session: Session | null } }) => {
      loadUserState(existingSession);
    });

    return () => subscription.unsubscribe();
  }, []);

  const user: AppUser | null = session?.user
    ? {
        id: session.user.id,
        name: profile?.first_name
          ? `${profile.first_name} ${profile.last_name || ''}`.trim()
          : session.user.email?.split('@')[0] || null,
        email: session.user.email || null,
        isAdmin: isAdminRole,
        accessLevel: profile?.access_level || 1,
        contractedServices: profile?.contracted_services || ['1'],
      }
    : null;

  const signIn = async (email: string, password: string) => {
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) throw error;

    // Demo account: enforce block + log login attempts
    if (email.trim().toLowerCase() === 'demo@creafinancia.es') {
      try {
        const { data: settings } = await supabase
          .from('demo_account_settings')
          .select('is_blocked')
          .eq('id', 1)
          .maybeSingle();
        if (settings?.is_blocked) {
          await supabase.auth.signOut();
          throw new Error('La cuenta de prueba está temporalmente deshabilitada.');
        }
        // Best-effort login log (do not block on errors)
        supabase.functions.invoke('manage-demo-account', {
          body: { action: 'log_login', user_agent: navigator.userAgent },
        }).catch(() => {});
      } catch (err) {
        if (err instanceof Error && err.message.includes('temporalmente deshabilitada')) throw err;
      }
    }

    return data;
  };

  const signUp = async (email: string, password: string, metadata?: Record<string, unknown>) => {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: metadata,
        emailRedirectTo: window.location.origin,
      },
    });
    if (error) throw error;
    return data;
  };

  const signOut = async () => {
    await supabase.auth.signOut();
  };

  return {
    user,
    session,
    profile,
    isAdmin: user?.isAdmin || false,
    isAuthenticated: !!session?.user,
    isLoading,
    mfaLevel,
    refreshMfaLevel,
    signIn,
    signUp,
    signOut,
  };
}
